/** 官网匿名归因交接协议；不参与身份、权限或结算判定。 */
export interface AttributionTouch {
  source: string;
  medium: string;
  campaign?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  gclid?: string;
  fbclid?: string;
  landing_page: string;
  referrer: string;
}
export interface RegistrationAttribution {
  visit_id: string;
  attribution_id: string;
  first_touch: AttributionTouch;
  last_touch: AttributionTouch;
  country?: string;
  market?: string;
}
export interface StoredRegistrationAttribution extends RegistrationAttribution {
  createdAt: number;
  updatedAt: number;
}
const QUERY_KEY = 'registration_attribution';
const RETENTION_MS = 90 * 86400000;
const WEBSITE_ORIGINS = new Set([
  'https://chatplus.ai',
  'https://www.chatplus.ai',
  'https://chatplus.ganjutech.com',
  'https://br-demo.chatplus.ai',
]);
const FIELDS = [
  'campaign',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'gclid',
  'fbclid',
] as const;
function record(value: unknown): Record<string, unknown> | undefined {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}
function pageUrl(value: unknown): string | undefined {
  if (value === '') return '';
  if (typeof value !== 'string') return;
  try {
    const url = new URL(value);
    if (!['https:', 'http:'].includes(url.protocol) || url.username || url.password) return;
    return `${url.origin}${url.pathname}`.slice(0, 1024);
  } catch {
    return;
  }
}
function parseTouch(value: unknown): AttributionTouch | undefined {
  const v = record(value);
  if (!v || typeof v.source !== 'string' || !v.source || typeof v.medium !== 'string' || !v.medium)
    return;
  const landing = pageUrl(v.landing_page);
  const referrer = pageUrl(v.referrer);
  if (!landing || referrer === undefined) return;
  const result: AttributionTouch = {
    source: v.source.slice(0, 256),
    medium: v.medium.slice(0, 256),
    landing_page: landing,
    referrer,
  };
  for (const key of FIELDS) {
    if (typeof v[key] === 'string' && v[key]) result[key] = v[key].slice(0, 256);
  }
  return result;
}
export function parseRegistrationAttribution(
  raw: string | null,
  now = Date.now(),
): StoredRegistrationAttribution | null {
  if (!raw || raw.length > 16000) return null;
  try {
    const v = record(JSON.parse(raw));
    if (
      !v ||
      typeof v.visit_id !== 'string' ||
      !/^[A-Za-z0-9_-]{1,120}$/.test(v.visit_id) ||
      typeof v.attribution_id !== 'string' ||
      !/^[A-Za-z0-9_-]{1,120}$/.test(v.attribution_id) ||
      typeof v.createdAt !== 'number' ||
      !Number.isFinite(v.createdAt) ||
      typeof v.updatedAt !== 'number' ||
      !Number.isFinite(v.updatedAt) ||
      v.createdAt > v.updatedAt ||
      v.updatedAt > now ||
      now - v.createdAt >= RETENTION_MS
    )
      return null;
    const first = parseTouch(v.first_touch);
    const last = v.last_touch === undefined ? first : parseTouch(v.last_touch);
    if (!first || !last) return null;
    return {
      visit_id: v.visit_id,
      attribution_id: v.attribution_id,
      first_touch: first,
      last_touch: last,
      createdAt: v.createdAt,
      updatedAt: v.updatedAt,
      ...(typeof v.country === 'string' && /^[A-Z]{2}$/.test(v.country)
        ? { country: v.country }
        : {}),
      ...(typeof v.market === 'string' && /^[A-Za-z0-9_-]{1,32}$/.test(v.market)
        ? { market: v.market }
        : {}),
    };
  } catch {
    return null;
  }
}
export function receiveRegistrationAttribution(
  href: string,
  referrer: string,
  stored: StoredRegistrationAttribution | null,
  now = Date.now(),
): { value: StoredRegistrationAttribution | null; cleanUrl: string } {
  const url = new URL(href);
  const incoming = url.searchParams.getAll(QUERY_KEY);
  url.searchParams.delete(QUERY_KEY);
  let value = stored ? parseRegistrationAttribution(JSON.stringify(stored), now) : null;
  let trusted = referrer === '';
  try {
    if (referrer) trusted = WEBSITE_ORIGINS.has(new URL(referrer).origin);
  } catch {
    /* 显式非法来源不导入。 */
  }
  const candidate =
    incoming.length === 1 && trusted ? parseRegistrationAttribution(incoming[0], now) : null;
  if (candidate && now - candidate.updatedAt < 5 * 60000) {
    if (!value) value = candidate;
    else {
      const first = candidate.createdAt < value.createdAt ? candidate : value;
      const last = candidate.updatedAt > value.updatedAt ? candidate : value;
      value = {
        ...first,
        last_touch: last.last_touch,
        visit_id: last.visit_id,
        updatedAt: last.updatedAt,
      };
    }
  }
  return { value, cleanUrl: url.href };
}
export function registrationAttributionPayload(
  value: StoredRegistrationAttribution | null,
  now = Date.now(),
): RegistrationAttribution | undefined {
  const valid = value && parseRegistrationAttribution(JSON.stringify(value), now);
  if (!valid) return;
  return {
    visit_id: valid.visit_id,
    attribution_id: valid.attribution_id,
    first_touch: valid.first_touch,
    last_touch: valid.last_touch,
    ...(valid.country ? { country: valid.country } : {}),
    ...(valid.market ? { market: valid.market } : {}),
  };
}
